import { useEffect, useRef, useState } from 'react';
import { toDate } from 'date-fns-tz';
import { Form } from 'informed';
import api from 'api';
import {
  getDefaultEmailTemplate,
  renderPlaceholdersInTemplates
} from 'shared-helpers/email';
import { useDispatch, useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { Button } from 'components/common';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalPortal
} from 'components/common/modal';
import { AdminsAddNewUsers, AdminsAddOrEditForm } from '.';

import type { FormApi } from 'informed';
import type { EmailTemplateData, UserPermission } from 'shared-types';
import type { GetEmailTemplatesByTypeRes } from 'shared-types/responses';

type ValuesInForm = {
  email: EmailTemplateData['email'] & { selectedTemplateIdentifier: string };
  emails: string[];
  ignoreNewUsers: boolean;
  permissions: Record<UserPermission, boolean>;
  role: 'admin' | 'superadmin';
  userExpirationDate?: Date | null;
};

interface Props {
  existingAdminIds?: number[];
  numberOfAdmins?: number;
  numberOfSuperadmins?: number;
  updateAdmins: () => Promise<void>;
}

const AdminsAdd = ({
  existingAdminIds,
  numberOfAdmins,
  numberOfSuperadmins,
  updateAdmins
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    language = 'en-GB',
    messages,
    serverConfig: {
      adminUrl,
      hideAchievementsFromUI,
      hideCalendarEventsFromUI,
      hideCoursesFromUI,
      hidePathsFromUI,
      timeZone,
      usersAdminLimit,
      usersSuperadminLimit
    },
    user
  } = useReduxState();

  const formApi = useRef<FormApi<ValuesInForm>>(undefined);

  const [isLoading, setIsLoading] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [newUsers, setNewUsers] = useState<string[]>([]);
  const [requiresStep2, setRequiresStep2] = useState(false);
  const [isStep2, setIsStep2] = useState(false);

  const { data: emailTemplatesData } = useApiData<GetEmailTemplatesByTypeRes>({
    url: `/admin/email-templates-data?type=new-user`
  });

  let emailTemplates = emailTemplatesData?.emailTemplates || [];

  emailTemplates = renderPlaceholdersInTemplates({
    emailTemplates,
    overridePlaceholderValues: {
      contactEmail: user?.email,
      createAccountUrl: adminUrl
    }
  });

  const emailTemplatesDefault = getDefaultEmailTemplate({
    language,
    emailTemplates
  });

  useEffect(() => {
    if (newUsers.length && !requiresStep2) setRequiresStep2(true);
    else if (!newUsers && requiresStep2) setRequiresStep2(false);
  }, [newUsers]);

  const handleHide = () => {
    setModalIsOpen(false);
    setIsStep2(false);
    setRequiresStep2(false);
    setIsLoading(false);
  };

  const handleSubmitClick = () => {
    const { errors, values } = formApi.current.getState();

    if (errors?.emails) return;

    if (!values?.emails?.length) {
      formApi.current.setError('emails', messages.commonFieldRequired);
      return;
    }

    formApi.current.submitForm();
  };

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      if (requiresStep2 && !isStep2) {
        setIsStep2(true);
      } else {
        setIsLoading(true);

        const permissions =
          values.permissions && values.role === 'admin'
            ? Object.keys(values.permissions).filter(
                (perm) => values.permissions[perm]
              )
            : [];

        const response = await api.post('/admin/admins', {
          emailContent: values.email,
          emails: values.emails || [],
          ignoreNewUsers: values.ignoreNewUsers || false,
          permissions,
          role: values.role,
          userExpirationDate: values.userExpirationDate
            ? toDate(values.userExpirationDate, { timeZone })
            : null
        });
        if (response.data?.errors?.existingAdmins?.length) {
          const text =
            `${messages.settingsAddAdminWarning}` +
            '\n' +
            response.data.errors.existingAdmins.join('\n');
          dispatch.addNotice({ text, type: 'warning' });
        }

        await updateAdmins();

        if (response.data?.numberOfAddedAdmins) {
          dispatch.addNotice({
            text: messages.settingsAddAdminSuccess,
            type: 'success'
          });
        }

        handleHide();
      }
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
      setIsLoading(false);
    }
  };

  const handleValidateEmails = (emails) => {
    const { role } = formApi.current.getState().values;

    if (
      role === 'admin' &&
      emails?.length &&
      emails.length + numberOfAdmins > usersAdminLimit
    ) {
      return messages.settingsAddAdminLicenseLimit;
    } else if (
      role === 'superadmin' &&
      emails?.length &&
      emails.length + numberOfSuperadmins > usersSuperadminLimit
    ) {
      return messages.settingsAddAdminLicenseLimit;
    }

    return undefined;
  };

  if (!emailTemplatesDefault) return null;

  const disableSuperadminOption = numberOfSuperadmins >= usersSuperadminLimit;

  const disableAdminOption = numberOfAdmins >= usersAdminLimit;

  const initialValues: ValuesInForm = {
    email: {
      ...emailTemplatesDefault.email,
      selectedTemplateIdentifier: emailTemplatesDefault.identifier
    },
    emails: [],
    ignoreNewUsers: false,
    permissions: {
      'create-achievements': hideAchievementsFromUI ? false : true,
      'create-courses': hideCoursesFromUI ? false : true,
      'create-groups': true,
      'create-paths': hidePathsFromUI ? false : true,
      'create-events': hideCalendarEventsFromUI ? false : true,
      'manage-users': true,
      'edit-user-custom-attributes': false,
      'use-ai-tools': false,
      'view-all-users': false,
      'manage-email-templates': false,
      'manage-competencies': false,
      'manage-competency-profiles': false,
      'grant-competencies': false
    },
    role: disableAdminOption ? 'superadmin' : 'admin'
  };

  return (
    <>
      <Button
        category="success"
        icon="plus"
        onClick={() => setModalIsOpen(true)}
        size="header"
        text={messages.settingsAddAdmins}
        {...(disableAdminOption && disableSuperadminOption
          ? {
              disabled: true,
              'aria-label': messages.usersAdminLicencesInUse,
              'data-tooltip': messages.usersAdminLicencesInUse
            }
          : {})}
      />

      <ModalPortal>
        <Modal
          onHide={handleHide}
          title={messages.settingsAddAdmins}
          visible={modalIsOpen}
          width={isStep2 ? 'wide' : 'medium'}
        >
          <ModalContent>
            <Form
              getApi={(form) =>
                (formApi.current = form as FormApi<ValuesInForm>)
              }
              initialValues={initialValues}
              onSubmit={handleSubmit}
            >
              {() =>
                isStep2 ? (
                  <AdminsAddNewUsers
                    emailTemplates={emailTemplates}
                    newUsers={newUsers}
                  />
                ) : (
                  <AdminsAddOrEditForm
                    disableAdmins={disableAdminOption}
                    disableSuperadmins={disableSuperadminOption}
                    existingAdminIds={existingAdminIds}
                    onNewUsers={setNewUsers}
                    validateEmails={handleValidateEmails}
                  />
                )
              }
            </Form>
          </ModalContent>

          <ModalFooter>
            <Button
              category="light"
              onClick={handleHide}
              text={messages.commonCancel}
            />

            <Button
              category="success"
              loading={isLoading}
              onClick={handleSubmitClick}
              text={isStep2 ? messages.commonConfirm : messages.commonAdd}
            />
          </ModalFooter>
        </Modal>
      </ModalPortal>
    </>
  );
};

export default AdminsAdd;
